// client/src/writer/hooks/useBackMatterAI.ts

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAppContext } from "../contexts/AppContext";
import { BookStructureTextElementKey } from "../types";

export const useBackMatterAI = () => {
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const { projectState, handleBackMatterChange } = useAppContext();

    const generateElementMutation = useMutation({
        mutationFn: async (elementType: BookStructureTextElementKey) => {
            const response = await apiRequest(`/api/llm/structure/${elementType}`, "POST", {
                // Pass all necessary context in the request body
                authorDetails: projectState?.authorDetails,
                outlineIdea: projectState?.outlineIdea,
                bookMetadata: projectState?.bookMetadata,
                worldBuildingData: projectState?.blueprint?.worldBuildingSuggestions,
                chapters: projectState?.chapters,
            });
            const result = await response.json();
            if (!result.success) {
                throw new Error(result.message || "Failed to generate element.");
            }
            return { elementType, data: result.data };
        },
        onSuccess: ({ elementType, data }) => {
            // The key in the returned data should match the element type, e.g., { epilogueContent: "..." }
            // We need to find the actual text from the response object.
            const contentKey = Object.keys(data).find(k => k.toLowerCase().includes(elementType.toLowerCase()));
            const generatedText = contentKey ? data[contentKey] : `Error: Could not find content for ${elementType}.`;

            if (contentKey) {
                 handleBackMatterChange(elementType as any, generatedText);
                 toast({
                    title: "AI Generation Successful",
                    description: `"${elementType}" has been generated.`,
                });
            } else {
                 toast({
                    title: "AI Generation Error",
                    description: `Received unexpected data structure from the server for ${elementType}.`,
                    variant: "destructive",
                });
            }
        },
        onError: (error: any) => {
            toast({
                title: "AI Generation Failed",
                description: error.message,
                variant: "destructive",
            });
        },
    });

    const handleGenerateElement = (elementType: BookStructureTextElementKey) => {
        generateElementMutation.mutate(elementType);
    };

    return {
        generateBackMatterElement: handleGenerateElement,
        isLoading: generateElementMutation.isPending,
    };
};